import conf from '../conf';
import { isProdEnv } from './helpers';

import type { Obligation } from '../../../types/ztb';

// Ported from home/client/src/utils/catalogConfig.js. Most learn-more links are static
// marketing pages that don't vary by environment; open-links that point back into
// AvaApp-hosted products resolve through buy-dot's own `conf`, which already differs by env.
const productCatalogUrls: Record<string, string> = {
  home: conf.HOME_URL,
  accountsPayable: `${conf.BUY_CLIENT_URL}/accounts-payable`,
  managedReturns: `${conf.CLIENT_URL}/returns/filings`,
  ecmOverview: isProdEnv() ? 'https://exemptions.avalara.com/search/vendors' : `${conf.CLIENT_URL}/exemptions/overview`,
  elrHome: 'https://app.avalara.com/elr-ui/overview',
  vatReturns: 'https://vatreporting.avalara.com/',
  atr: 'https://my.ttrus.com/user/login',
  excise: 'https://excise.avalara.com/',
  comms: 'https://communications.avalara.net/',
  brazilTaxSuite: 'https://portal.avalarabrasil.com.br/Login/Index',
  propertyTax: 'https://ptrp.avalara.com/',
  lodging: 'https://app.mylodgetax.avalara.com/admin/login.aspx',
  '1099': 'https://www.avalara.com/us/en/products/1099.html',
  businessLicenses: 'https://www.businesslicenses.com/admin_login.php',
  tariffCode: 'https://classify.prod.3ceonline.com/ui/login?msg=mustlogin',
  avataxMarketing: 'https://www.avalara.com/us/en/products/calculations.html',
  accountsPayableMarketing: 'https://www.avalara.com/us/en/products/avatax-for-accounts-payable.html',
  managedReturnsMarketing: 'https://www.avalara.com/us/en/products/sales-and-use-tax/returns.html',
  eInvoiceMarketing: 'https://www.avalara.com/us/en/products/e-invoicing.html',
  ecmMarketing: 'https://www.avalara.com/us/en/products/sales-and-use-tax/compliance-document-management.html',
  ecmVendorsMarketing: 'https://www.avalara.com/us/en/products/vendor-exemption-management.html',
  vatMarketing: 'https://www.avalara.com/us/en/products/vat-returns-and-reporting.html',
  taxResearchMarketing: 'https://www.avalara.com/us/en/products/avalara-tax-research.html',
  exciseMarketing: 'https://www.avalara.com/us/en/products/industry-solutions/energy-tax-solutions.html',
  commsMarketing: 'https://www.avalara.com/us/en/products/communications-tax.html',
  brazilTaxSuiteMarketing: 'https://www.avalara.com/br/en/index.html',
  propertyTaxMarketing: 'https://www.avalara.com/us/en/products/property-tax.html',
  lodgingMarketing: 'https://www.avalara.com/us/en/products/lodging-tax.html',
  '1099Marketing': 'https://www.avalara.com/us/en/products/1099.html',
  businessLicensesMarketing: 'https://www.avalara.com/us/en/products/business-licenses.html',
  tariffCodeMarketing: 'https://www.avalara.com/us/en/products/tariff-code-classification.html',
  crossBorderComplianceMarketing:
    'https://www.avalara.com/us/en/products/industry-solutions/cross-border-solutions.html',
  salesTaxRegistrationMarketing: 'https://www.avalara.com/us/en/products/business-licenses/sales-tax-registration.html',
  voluntaryDisclosureAgreementsMarketing: 'https://www.avalara.com/us/en/products/professional-services.html',
  crushMarketing: 'https://www.avalara.com/us/en/products/crush.html',
};

export const getResolvedUrl = (urlKey?: string): string => {
  if (!urlKey) return '';
  return productCatalogUrls[urlKey] || '';
};

// Ported from home/client/src/components/Catalog/ProductCatalogPage.tsx's hasProductAccess.
// Buy-dot's own account.obligations (populated by fetchAccountData) carries the same
// productCatalogId field Home reads from its Boomi obligations, so no new fetch is needed.
// Unlike Home, buy-dot has no sandbox environment, so (unlike the ported original) this
// doesn't filter productCatalogId by a `_SBX` suffix — it matches obligations against the
// full list of catalog IDs for the product, sandbox-suffixed or not.
export const hasProductAccess = (productCatalogId: Array<string>, obligations: Array<Obligation>): boolean => {
  if (!productCatalogId.length) return false;

  const userObligationIds = obligations.map((o) => o.productCatalogId);
  return productCatalogId.some((id) => userObligationIds.includes(id));
};
